import { generateApiPath } from '@taxfix/scouts-helpers'

import {
    ProfileDTO,
    SelfAssessmentSubscriptionTiers,
    SubscriptionCancellationType,
    SubscriptionDTO,
    SubscriptionPaymentDTO,
    SubscriptionPaymentSessionId,
    SubscriptionTiers,
    SubscriptionTierDTO,
    UserAgreementDTO,
} from '@/models'

import { apiSlice } from '../api-slice'
import { ENDPOINTS } from '../endpoints'
import { Tags } from '../tags'

export interface InitiateSubscriptionPaymentParams {
    subscriptionId: SubscriptionDTO['id']
    tier: SubscriptionTiers
    redirectUrl: string
}

export interface SubscriptionCheckoutParams {
    token?: string
    paymentType: 'SelfAssessment'
    selfAssessmentId: number
    productTierType: SelfAssessmentSubscriptionTiers
    redirectUrl: string
}

export interface ConfirmSubscriptionPaymentParams {
    sessionId: SubscriptionPaymentSessionId
}

export interface GetSubscriptionPaymentsParams {
    subscriptionId: SubscriptionDTO['id']
}

export interface CreateSubscriptionRequestParamsDTO {
    userId: ProfileDTO['id']
    tier: SubscriptionTiers
}

export interface CancelSubscriptionParams {
    subscriptionId: SubscriptionDTO['id']
    cancellationReasonType?: keyof typeof SubscriptionCancellationType
    cancellationReasonDescription?: string
}

export interface AssignSubscriptionAccountantParams {
    subscriptionId: SubscriptionDTO['id']
}

export interface GetSubscriptionAgreementParams {
    subscriptionId: SubscriptionDTO['id']
}

const extendedApi = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getActiveSubscription: builder.query<SubscriptionDTO, void>({
            query: () => ({
                method: 'GET',
                url: ENDPOINTS.SUBSCRIPTIONS.GET.ACTIVE,
            }),
            providesTags: [Tags.Subscriptions],
        }),
        getSubscriptionPayments: builder.query<SubscriptionPaymentDTO[], GetSubscriptionPaymentsParams>({
            query: ({ subscriptionId }) => ({
                method: 'GET',
                url: generateApiPath(ENDPOINTS.SUBSCRIPTIONS.GET.PAYMENTS, { subscriptionId }),
            }),
            providesTags: [Tags.Subscriptions],
        }),
        createSubscription: builder.mutation<SubscriptionDTO, CreateSubscriptionRequestParamsDTO>({
            query: (data) => ({
                method: 'POST',
                url: ENDPOINTS.SUBSCRIPTIONS.POST.CREATE,
                data,
            }),
        }),
        initiateSubscriptionPayment: builder.mutation<SubscriptionPaymentSessionId, InitiateSubscriptionPaymentParams>({
            query: (data) => ({
                method: 'POST',
                url: ENDPOINTS.SUBSCRIPTIONS.POST.INITIATE_PAYMENT,
                data,
            }),
        }),
        subscriptionCheckout: builder.mutation<{ sessionId: SubscriptionPaymentSessionId }, SubscriptionCheckoutParams>(
            {
                query: (data) => ({
                    method: 'POST',
                    url: ENDPOINTS.PAYMENT.POST.SUBSCRIPTION_CHECKOUT,
                    data,
                }),
            }
        ),
        confirmSubscriptionPayment: builder.mutation<SubscriptionDTO, ConfirmSubscriptionPaymentParams>({
            query: ({ sessionId }) => ({
                method: 'POST',
                url: generateApiPath(ENDPOINTS.SUBSCRIPTIONS.POST.CONFIRM_PAYMENT, { sessionId }),
            }),
            invalidatesTags: [Tags.Subscriptions],
        }),
        cancelSubscription: builder.mutation<void, CancelSubscriptionParams>({
            query: ({ subscriptionId, ...data }) => ({
                method: 'PUT',
                url: generateApiPath(ENDPOINTS.SUBSCRIPTIONS.PUT.CANCEL, { subscriptionId }),
                data,
            }),
            invalidatesTags: (_1, error) => {
                if (error) return []
                return [Tags.Subscriptions]
            },
        }),
        assignSubscriptionAccountant: builder.mutation<SubscriptionDTO, AssignSubscriptionAccountantParams>({
            query: ({ subscriptionId }) => ({
                method: 'POST',
                url: generateApiPath(ENDPOINTS.SUBSCRIPTIONS.POST.ASSIGN_ACCOUNTANT, { subscriptionId }),
            }),
            invalidatesTags: [Tags.Subscriptions],
        }),
        getSubscriptionTiers: builder.query<SubscriptionTierDTO[], void>({
            query: () => ({
                method: 'GET',
                url: ENDPOINTS.SUBSCRIPTIONS.GET.TIERS,
            }),
            providesTags: [Tags.SubscriptionTiers],
        }),
        getSubscriptionAgreement: builder.query<UserAgreementDTO, GetSubscriptionAgreementParams>({
            query: ({ subscriptionId }) => ({
                method: 'GET',
                url: generateApiPath(ENDPOINTS.SUBSCRIPTIONS.GET.AGREEMENT, {
                    subscriptionId,
                }),
            }),
        }),
    }),
})

export const {
    useInitiateSubscriptionPaymentMutation,
    useSubscriptionCheckoutMutation,
    useConfirmSubscriptionPaymentMutation,
    useGetActiveSubscriptionQuery,
    useCreateSubscriptionMutation,
    useGetSubscriptionPaymentsQuery,
    useCancelSubscriptionMutation,
    useAssignSubscriptionAccountantMutation,
    useGetSubscriptionTiersQuery,
    useGetSubscriptionAgreementQuery,
} = extendedApi
